import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft, Plus } from 'lucide-react';
import { updateProjectContext } from '../../../AGTHall/services/AGTHallService';
import { useProjectContext } from '../ProjectContextProvider';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

const StyledButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '20px',
    textTransform: 'none',
    padding: '6px 16px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.8)' : '#0371db',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: 6,
    },
  };
});

// Definimos los límites de caracteres
const MAX_TITLE_CHARS = 50;
const MAX_CONTEXT_CHARS = 1000;

const ContextView = ({ project, onBack, user }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation(); // Inicializar hook de traducción
  const { projectContexts, updateProjectContext: updateContextInProvider } = useProjectContext();

  // Obtener el contexto del provider
  const currentContext = projectContexts[project?.id] || {};
  const realContextTitle = currentContext.contextTitle || '';
  const realContextContent = currentContext.contextContent || '';

  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [title, setTitle] = useState('');
  const [context, setContext] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  // Cargar contexto inicial
  useEffect(() => {
    if (project?.id && project?.contextData) {
      updateContextInProvider(project.id, {
        contextTitle: project.contextData.contextTitle || '',
        contextContent: project.contextData.contextContent || ''
      });
    }
  }, [project?.id, project?.contextData, updateContextInProvider]);

  const handleAddContext = () => {
    setOpenModal(true);
    setTitle('');
    setContext('');
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setTitle('');
    setContext('');
  };

  const handleSave = async () => {
    if (!project?.id || !user?.uid) return;

    setIsLoading(true);
    const finalTitle = title.trim() || t('projectViews.context.newContext');

    try {
      await updateProjectContext({
        userId: user.uid,
        projectId: project.id,
        contextTitle: finalTitle,
        contextContent: context
      });

      // Actualizar el contexto en el provider
      updateContextInProvider(project.id, {
        contextTitle: finalTitle,
        contextContent: context
      });

      setSnackbarMessage(t('projectViews.context.contextSaved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseModal();

    } catch (error) {
      console.error('Error saving context:', error);
      setSnackbarMessage(t('projectViews.context.contextSaveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleOpenViewModal = () => {
    if (realContextTitle || realContextContent) {
      setOpenViewModal(true);
    }
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: '800px',
            lg: '900px'
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                mr: 1,
                color: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.8)'
                  : (theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700')
              }}
            >
              <ArrowLeft />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 500,
                color: isWallpaperMode ? '#FFFFFF' : 'inherit'
              }}
            >
              {t('projectViews.context.title')}
            </Typography>
          </Box>

          <StyledButton
            startIcon={<Plus size={18} />}
            onClick={handleAddContext}
            disabled={isLoading}
          >
            {t('projectViews.context.newContext')}
          </StyledButton>
        </Box>

        <List sx={{ px: 0 }}>
          {!realContextTitle && !realContextContent ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              {t('projectViews.context.noContext')}
            </Typography>
          ) : (
            <ListItem
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: '12px',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                backgroundColor:
                  isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.12)'
                    : (theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.03)'
                      : 'rgba(0, 0, 0, 0.02)'),
                '&:hover': {
                  backgroundColor:
                    isWallpaperMode
                      ? 'rgba(255, 255, 255, 0.18)'
                      : (theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.05)'),
                },
                transition: 'background-color 0.2s ease',
              }}
              onClick={handleOpenViewModal}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      mb: 0.5,
                      color: isWallpaperMode ? '#FFFFFF' : 'inherit'
                    }}
                  >
                    {realContextTitle || t('projectViews.context.untitledContext')}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      color: isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.7)'
                        : (theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.5)'
                          : 'rgba(0, 0, 0, 0.5)')
                    }}
                  >
                    {realContextContent.length > 60
                      ? `${realContextContent.slice(0, 60)}...`
                      : realContextContent}
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Box>

      {/* Modal para nuevo contexto */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
            backgroundImage: 'none',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            pb: 2,
            px: 3,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderBottom: 1,
            borderColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.2)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'),
            color: isWallpaperMode ? '#FFFFFF' : 'inherit'
          }}
        >
          {t('projectViews.context.newContext')}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          {/* Título con límite de 50 caracteres */}
          <TextField
            autoFocus
            fullWidth
            placeholder={t('projectChatView.contextTitle')}
            value={title}
            onChange={(e) => {
              const newTitle = e.target.value.slice(0, MAX_TITLE_CHARS);
              setTitle(newTitle);
            }}
            variant="outlined"
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': { border: 'none' },
              }
            }}
          />
          {/* Contador para el título */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 2,
              textAlign: 'right',
              color: title.length >= MAX_TITLE_CHARS ? 'red' : 'inherit',
            }}
          >
            {MAX_TITLE_CHARS - title.length}
          </Typography>

          {/* Context con límite de 1000 caracteres */}
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t('projectChatView.contextPlaceholder')}
            value={context}
            onChange={(e) => {
              const newContext = e.target.value.slice(0, MAX_CONTEXT_CHARS);
              setContext(newContext);
            }}
            variant="outlined"
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)'),
                border: 'none',
                '& fieldset': { border: 'none' },
                '& textarea': {
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: isWallpaperMode
                      ? 'rgba(255, 255, 255, 0.4)'
                      : (theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'rgba(0, 0, 0, 0.2)'),
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: isWallpaperMode
                      ? 'rgba(255, 255, 255, 0.55)'
                      : (theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)'),
                  },
                },
              }
            }}
          />
          {/* Contador para el contexto */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 2,
              textAlign: 'right',
              color: context.length >= MAX_CONTEXT_CHARS ? 'red' : 'inherit',
            }}
          >
            {MAX_CONTEXT_CHARS - context.length}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 2,
              color: isWallpaperMode
                ? 'rgba(255, 255, 255, 0.7)'
                : (theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'rgba(0, 0, 0, 0.5)'),
              fontSize: '0.7rem',
              textAlign: 'center'
            }}
          >
            {t('projectChatView.contextNote')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: 3,
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              '&:hover': {
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)')
              }
            }}
          >
            {t('projectViews.context.cancel')}
          </Button>
          <StyledButton 
            onClick={handleSave}
            disabled={isLoading || !context.trim()}
          >
            {t('projectViews.context.save')}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Modal para ver contexto */}
      <Dialog
        open={openViewModal}
        onClose={handleCloseViewModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: isWallpaperMode
              ? 'rgba(128, 128, 128, 0.3)'
              : (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
            backgroundImage: 'none',
            border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
            backdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
            WebkitBackdropFilter: isWallpaperMode ? 'blur(12px)' : 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            pb: 2,
            px: 3,
            fontSize: '1.1rem',
            fontWeight: 500,
            borderBottom: 1,
            borderColor: isWallpaperMode
              ? 'rgba(255, 255, 255, 0.2)'
              : (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'),
            color: isWallpaperMode ? '#FFFFFF' : 'inherit'
          }}
        >
          {realContextTitle || t('projectViews.context.untitledContext')}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, p: 3 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {realContextContent}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseViewModal}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: 3,
              color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              '&:hover': {
                backgroundColor: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : (theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)')
              }
            }}
          >
            {t('projectViews.context.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContextView;
